<template>
    <div class="mb-root">
        <div v-if="infoCard" class="msg-card" :class="[`msg-card--${infoCard.kind}`, { 'msg-card--alert': infoCard.alert }]">
            <div class="msg-card-head">
                <span class="msg-card-ic"><v-icon size="12">{{ infoCard.icon }}</v-icon></span>
                <span class="msg-card-type">{{ infoCard.label }}</span>
                <span v-if="infoCard.date" class="msg-card-date">{{ infoCard.date }}</span>
            </div>
            <div v-if="infoCard.badge || infoCard.origin" class="msg-card-badge-wrap">
                <span v-if="infoCard.badge" class="msg-card-badge">{{ infoCard.badge }}</span>
                <span v-if="infoCard.origin" class="msg-card-origin"
                    :class="{ 'msg-card-origin--competence': infoCard.origin.isCompetence }">
                    <v-icon size="11">{{ infoCard.origin.icon }}</v-icon><span>{{ infoCard.origin.label }}</span>
                </span>
            </div>
            <div v-if="infoCard.rows.length" class="msg-card-body">
                <div class="msg-card-row" v-for="(row, i) in infoCard.rows" :key="i">
                    <span class="msg-card-lbl">{{ row.label }}</span>
                    <span class="msg-card-val">{{ row.value }}</span>
                </div>
            </div>
            <div v-if="message.assignedRoutineId || (infoCard.kind === 'injury' && isCoach)" class="msg-card-footer">
                <template v-if="message.assignedRoutineId">
                    <button type="button" class="msg-card-btn" @click="loadAssignedRoutineSummary">
                        <v-icon size="13">mdi-eye-outline</v-icon>
                        {{ $t('common.see') }}
                    </button>
                    <button v-if="isCoach" type="button" class="msg-card-btn" :disabled="loadingRoutine"
                        @click="goToEditRoutine">
                        <v-icon size="13">mdi-file-document-edit-outline</v-icon>
                        {{ $t('common.edit') }}
                    </button>
                </template>
                <button v-else-if="infoCard.kind === 'injury' && isCoach" type="button" class="msg-card-btn"
                    @click="showComplexityDialog">
                    <v-icon size="13">mdi-list-status</v-icon>
                    {{ $t('common.complexity') }}
                </button>
            </div>
        </div>

        <div v-else :class="from === 'me' ? 'bubble-grey' : 'bubble'">
            <a v-if="message.type === 'attachment'" class="attachment" :href="message.file.downloadURL" target="_blank"
                rel="noopener noreferrer" :download="message.message">
                <v-icon size="16" class="attachment-ic">mdi-paperclip</v-icon>
                <span class="attachment-name">{{ message.message }}</span>
                <v-icon size="15" class="attachment-dl">mdi-download</v-icon>
            </a>
            <div v-else-if="message.type === 'attachment_video'" class="p-relative">
                <div v-if="videoError">
                    <i style="font-size: 12px;">
                        {{ $t('common.video_unavailable') }}
                    </i>
                    <div style="max-width: 200px;">{{ message.message }}</div>
                </div>
                <template v-else>
                    <video :src="message.file.downloadURL" width="200" height="200" controls playsinline
                        style="border-radius: 4px;" @error="handleVideoError" />
                    <div style="max-width: 200px;">{{ message.message }}</div>
                    <div class="btn-info">
                        <button-tooltip color="surface2" fab x-small depressed
                            :tooltip="$t('tooltips.user_video_delete_info')">
                            <v-icon color="primary">mdi-information-outline</v-icon>
                        </button-tooltip>
                    </div>
                </template>
            </div>
            <div v-else-if="message.type === 'audio'" class="audio-msg">
                <audio-player :src="message.message" />
            </div>
            <span v-else>{{ message.message }}</span>
        </div>

        <summary-assigned-routine-dialog is-second-dialog back-button :assignedRoutineId="assignedRoutineId"
            :is-competence="isCompetence" ref="summary" hide-back-routines />
        <injury-complexity-dialog ref="complexity" is-second-dialog />
        <add-competence-dialog v-if="isCoach && message.assignedRoutineId" collection="assignedRoutines"
            :coach-id="coachId" ref="competence" />
    </div>
</template>

<script>
import InjuryComplexityDialog from '../../statistics/injuries/InjuryComplexityDialog.vue';
import SummaryAssignedRoutineDialog from '../../student/SummaryAssignedRoutineDialog.vue';
import AddCompetenceDialog from '../../coach/competences/AddCompetenceDialog.vue';
import ButtonTooltip from '../ButtonTooltip.vue'
import AudioPlayer from '../AudioPlayer.vue'
import { db } from '@/utils/Firebase'
export default {
    components: { ButtonTooltip, SummaryAssignedRoutineDialog, InjuryComplexityDialog, AudioPlayer, AddCompetenceDialog },
    props: {
        message: {
            type: Object,
            default: null,
        },
        from: {
            type: String,
            default: 'me',
        }
    },
    data() {
        return {
            videoError: false,
            assignedRoutineId: null,
            assignedRoutine: null,
            loadingRoutine: false,
        };
    },
    computed: {
        isCoach(vm = this) {
            return vm.accountType === 'coach'
        },
        originIsCompetence(vm = this) {
            if (typeof vm.message?.isCompetence === 'boolean') return vm.message.isCompetence
            if (vm.assignedRoutine) return vm.assignedRoutine.type === 'competence'
            return null
        },
        isCompetence(vm = this) {
            return vm.originIsCompetence === true
        },
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        },
        infoCard(vm = this) {
            const m = vm.message
            if (!m) return null
            if (['attachment', 'attachment_video', 'audio'].includes(m.type)) return null

            const isInjury = m.type === 'injury'
            const isWellness = Array.isArray(m.preSummary) && m.preSummary.length > 0
            const isSession = !!m.assignedRoutineId && !isWellness && !isInjury
            const lines = (m.message || '').split('\n').map(l => l.trim()).filter(Boolean)
            const looksStructured = lines.length >= 2 && lines.filter(l => l.includes(':')).length >= 2
            if (!isWellness && !isSession && !isInjury && !looksStructured) return null

            let title = null
            let date = null
            let badge = null
            const rows = []
            for (const line of lines) {
                const idx = line.indexOf(':')
                if (idx === -1) { if (!title) title = line; continue }
                const label = line.slice(0, idx).trim()
                const value = line.slice(idx + 1).trim()
                if (!date && /^\d{1,2}-\d{1,2}-\d{4}/.test(value)) { date = value; continue }
                if (isWellness && /wellness/i.test(label)) {
                    badge = (value.match(/[\d.,/]+/) || [value])[0]
                    continue
                }
                rows.push({ label, value })
            }

            let kind = 'activity'
            let icon = 'mdi-run'
            let label = title || vm.$t('common.activity')
            if (isWellness) {
                kind = 'wellness'
                icon = 'mdi-heart-pulse'
                label = vm.$t('common.wellness')
            } else if (isInjury) {
                kind = 'injury'
                icon = 'mdi-bandage'
                label = vm.$t('common.injury')
            } else if (isSession) {
                kind = 'session'
                icon = 'mdi-clipboard-text-outline'
                label = title || vm.$t('routines.summary')
            }

            let origin = null
            if ((isWellness || isSession) && vm.originIsCompetence !== null) {
                origin = vm.originIsCompetence
                    ? { isCompetence: true, icon: 'mdi-trophy-outline', label: vm.$t('common.competence') }
                    : { isCompetence: false, icon: 'mdi-dumbbell', label: vm.$t('common.routine') }
            }

            return { kind, icon, label, date, badge, rows, origin, alert: !!m.color || isInjury }
        }
    },
    methods: {
        handleVideoError() {
            this.videoError = true;
        },
        async loadAssignedRoutine() {
            const vm = this
            if (vm.assignedRoutine) return vm.assignedRoutine
            if (!vm.message?.assignedRoutineId) return null
            await vm.withLoading(async () => {
                const ref = await db.collection('assignedRoutines').doc(vm.message.assignedRoutineId).get()
                if (!ref.exists) return
                vm.assignedRoutine = { ...ref.data(), id: ref.id }
            }, 'loadingRoutine')
            return vm.assignedRoutine
        },
        async loadAssignedRoutineSummary() {
            const vm = this
            if (vm.originIsCompetence === null) await vm.loadAssignedRoutine()
            vm.assignedRoutineId = vm.message.assignedRoutineId
            if (!vm.$refs.summary) return
            vm.$refs.summary.show()
        },
        async goToEditRoutine() {
            const vm = this
            const routine = await vm.loadAssignedRoutine()
            if (!routine) return vm.toast(vm.$t('common.an_error_has_occurred'), 'error')

            if (vm.isCompetence) return vm.$refs.competence.show(routine.rawDate?.full || null, routine)

            vm.pushRoute({ name: 'coach.routines.edit.assigned', params: { uid: vm.message.assignedRoutineId } })
        },
        showComplexityDialog() {
            const vm = this;
            vm.$refs.complexity.show(vm.message)
        }
    },
}
</script>

<style scoped>
.mb-root {
    display: contents;
}

.bubble {
    position: relative;
    max-width: 75%;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    border-bottom-left-radius: 4px;
    padding: 9px 13px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--v-txtPrimary-base);
    display: inline-block;
    word-break: break-word;
    white-space: pre-line;
}

.bubble-grey {
    position: relative;
    max-width: 75%;
    background: rgba(51, 153, 153, 0.18);
    border: 1px solid rgba(51, 153, 153, 0.25);
    border-radius: 12px;
    border-bottom-right-radius: 4px;
    padding: 9px 13px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--v-txtPrimary-base);
    display: inline-block;
    word-break: break-word;
    white-space: pre-line;
}

a {
    color: var(--v-accent-base);
}

.attachment {
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    color: var(--v-txtPrimary-base);
    max-width: 240px;
}

.attachment-ic {
    color: var(--v-accent-base) !important;
    flex-shrink: 0;
}

.attachment-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    text-decoration: underline;
}

.attachment-dl {
    color: var(--v-txtMuted-base) !important;
    flex-shrink: 0;
}

.audio-msg {
    width: 240px;
    max-width: 100%;
}

.btn-info {
    position: absolute;
    top: 0;
    right: 0;
}

.msg-card {
    max-width: 290px;
    border: 1px solid var(--v-outline-base);
    border-radius: 11px;
    background: var(--v-surface-base);
    overflow: hidden;
}

.msg-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px 7px;
    border-bottom: 1px solid var(--v-outline-base);
}

.msg-card-ic {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.msg-card-type {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.msg-card-date {
    font-size: 10px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    flex-shrink: 0;
}

.msg-card-badge-wrap {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 12px;
    justify-content: center;
}

.msg-card-origin {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    padding: 0 8px;
    border-radius: 20px;
    border: 1px solid var(--v-outline-base);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: var(--v-txtSecondary-base);
}

.msg-card-origin .v-icon {
    color: inherit;
    display: block;
}

.msg-card-origin--competence {
    border-color: rgba(124, 58, 237, 0.5);
    background: rgba(124, 58, 237, 0.12);
    color: #a78bfa;
}

.theme--light .msg-card-origin--competence {
    color: #7c3aed;
}

.msg-card-badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
}

.msg-card-body {
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.msg-card-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
}

.msg-card-lbl {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.msg-card-val {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    margin-left: auto;
    text-align: right;
    word-break: break-word;
}

.msg-card-footer {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border-top: 1px solid var(--v-outline-base);
}

.msg-card-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 10px;
    height: 26px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: transparent;
    font-family: inherit;
    font-size: 11px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: background .15s, color .15s;
}

.msg-card-btn:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.msg-card-btn:disabled {
    opacity: .5;
    cursor: default;
}

.msg-card-btn .v-icon {
    color: inherit;
}

.msg-card--session .msg-card-ic,
.msg-card--wellness .msg-card-ic {
    background: rgba(51, 153, 153, 0.18);
    color: var(--v-accent-base);
}

.msg-card--session .msg-card-type,
.msg-card--wellness .msg-card-type {
    color: var(--v-accent-base);
}

.msg-card--activity .msg-card-ic {
    background: rgba(102, 204, 204, 0.18);
    color: var(--v-secondary-base);
}

.msg-card--activity .msg-card-type {
    color: var(--v-secondary-base);
}

.msg-card-badge {
    background: rgba(51, 153, 153, 0.16);
    color: var(--v-accent-base);
}

.msg-card--alert .msg-card-ic {
    background: rgba(232, 92, 92, 0.18);
    color: var(--v-error-base);
}

.msg-card--alert .msg-card-type {
    color: var(--v-error-base);
}

.msg-card--alert .msg-card-badge {
    background: rgba(232, 92, 92, 0.16);
    color: var(--v-error-base);
}
</style>
